import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { environment } from '../environments/environment';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from './material/material.module';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { BehaviorSubject } from 'rxjs';
import { TestTokenInterceptor } from './interceptors/test-token.interceptor';
import { TestMsalService } from './services/test-msal.service';
import { TestLoginDialogComponent } from './test-login-dialog/test-login-dialog.component';

import { AuditDialogComponent } from './audit-dialog/audit-dialog.component';
import { BarcodeDisplayComponent } from './barcode-display/barcode-display.component';
import { BarcodePrintComponent } from './barcode-print/barcode-print.component';
import { AdvancedBundleDialogComponent } from './bundles/advanced-bundle-dialog/advanced-bundle-dialog.component';
import { AdvancedBundleEditComponent } from './bundles/advanced-bundle-edit/advanced-bundle-edit.component';
import { BundleListComponent } from './bundles/bundle-list/bundle-list.component';
import { BundleComponent } from './bundles/bundle/bundle.component';
import { CellEditDisplayComponent } from './cell-edit-display/cell-edit-display.component';
import { ChangesDialogComponent } from './changes-dialog/changes-dialog.component';
import { ConditionListComponent } from './conditions/condition-list/condition-list.component';
import { CsvFormComponent } from './csv-form/csv-form.component';
import { DetailListPrintAveryComponent } from './detail/detail-list-print-avery/detail-list-print-avery.component';
import { DetailListPrintBarcodesComponent } from './detail/detail-list-print-barcodes/detail-list-print-barcodes.component';
import { DetailListPrintZebraComponent } from './detail/detail-list-print-zebra/detail-list-print-zebra.component';
import { DetailListPrintComponent } from './detail/detail-list-print/detail-list-print.component';
import { DetailListComponent } from './detail/detail-list/detail-list.component';
import { ExperimentDetailComponent } from './experiment-detail/experiment-detail.component';
import { ExperimentDialogComponent } from './experiments/experiment-dialog/experiment-dialog.component';
import { ExperimentListComponent } from './experiments/experiment-list/experiment-list.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { InputsComponent } from './inputs-list/inputs.component';
import { LogisticListComponent } from './logistics/logistic-list/logistic-list.component';
import { LogisticComponent } from './logistics/logistic/logistic.component';
import { MatAckDialogComponent } from './mat-ack-dialog/mat-ack-dialog.component';
import { MatConfirmDialogComponent } from './mat-confirm-dialog/mat-confirm-dialog.component';
import { NoAccessComponent } from './noaccess/noaccess.component';
import { NoteDisplayComponent } from './note-display/note-display.component';
import { NotesDialogComponent } from './notes-dialog/notes-dialog.component';
import { AssayTypeCodeFormatPipe } from './pipes/assayTypeCode-format.pipe';
import { BundleAssayTypeFormatPipe } from './pipes/bundle-assayType-format.pipe';
import { ExperimentFormatPipe } from './pipes/experiment-format.pipe';
import { ReleaseNotesDialogComponent } from './release-notes-dialog/release-notes-dialog.component';
import { ChooseExperimentComponent } from './results/choose-experiment/choose-experiment.component';
import { DetailsResultsComponent } from './results/details-results/details-results.component';
import { SavannaResultsComponent } from './results/savanna-results/savanna-results.component';
import { SampleListComponent } from './samples/sample-list/sample-list.component';
import { ScanDisplayComponent } from './scan-display/scan-display.component';
import { ErrorHandlerService } from './services/error-handler-service.service';
import { LoggingService } from './services/logging.service';
import { BundleService } from './shared/bundle.service';
import { ExperimentService } from './shared/experiment.service';
import { UserDisplayComponent } from './user-display/user-display.component';
import { XlsImportComponent } from './xls-import/xls-import.component';

import { AddColumnDialogComponent } from './add-column-dialog/add-column-dialog.component';
import { SavannaResultsImgSrcPipe } from './pipes/savanna-results-img-src.pipe';
import { SavannaConfigComponent } from './savanna-config/savanna-config.component';
import { SavannaCreateConfigDialogComponent } from './savanna-config/savanna-create-config-dialog/savanna-create-config-dialog.component';

import { ExperimentStatusIsNotPipe } from './pipes/experiment-status-is-not.pipe';
import { ExperimentStatusIsPipe } from './pipes/experiment-status-is.pipe';
import { HasAnyRolesPipe } from './pipes/has-any-roles.pipe';

import { DatePipe } from '@angular/common';
import { providePrimeNG } from 'primeng/config';
import { AutomatedReportsComponent } from './automated-reports/automated-reports.component';
import { FixDuplicateResultsComponent } from './automated-reports/fix-duplicate-results/fix-duplicate-results.component';
import { SelectExternalControlComponent } from './automated-reports/select-external-control/select-external-control.component';
import { ExperimentListForBundleComponent } from './bundles/experiment-list-for-bundle/experiment-list-for-bundle.component';
import { ColumnFilterComponent } from './column-filter/column-filter.component';
import { ConditionsPrecisionGenerationComponent } from './conditions/conditions-precision-generation/conditions-precision-generation.component';
import { ContactAndSupportDialogComponent } from './contact-and-support-dialog/contact-and-support-dialog.component';
import { DeviceTargetErrorDialogComponent } from './device-target-error-dialog/device-target-error-dialog.component';
import { DeviceTargetErrorChannelDialogComponent } from './device-target-error-dialog/device-target-error-dialog/device-target-error-dialog.component';
import { IntegerOnlyDirective } from './directives/integer-only.directive';
import { ICExcludeIncludeDialogComponent } from './image-capture/adjust-legend/exclude-include-dialog/ic-exclude-include-dialog.component';
import { ICAdjustLegendComponent } from './image-capture/adjust-legend/ic-adjust-legend.component';
import { ICExperimentDetailComponent } from './image-capture/experiment-detail/ic-experiment-detail.component';
import { ReplaceInstrumentComponent } from './image-capture/experiment-detail/replace-instrument/replace-instrument.component';
import { ICExperimentDialogComponent } from './image-capture/experiment-dialog/ic-experiment-dialog.component';
import { ICExperimentsComponent } from './image-capture/experiments/ic-experiments.component';
import { GroupDropdownComponent } from './image-capture/group-dropdown/group-dropdown.component';
import { ICImagesDialogComponent } from './image-capture/images-dialog/ic-images-dialog.component';
import { ICSequenceComponent } from './image-capture/sequence/ic-sequence.component';
import { ICSyncDeviceComponent } from './image-capture/sync-device/ic-sync-device.component';
import { InstrumentErrorDialogComponent } from './instrument-error-dialog/instrument-error-dialog.component';
import { HttpRequestCountInterceptor } from './interceptors/http-request-count.interceptor';
import { LoaderComponent } from './loader/loader.component';
import { AssayDialogComponent } from './manage-assays/assay-dialog/assay-dialog.component';
import { ManageAssaysComponent } from './manage-assays/manage-assays.component';
import { BarcodeDialogComponent } from './mobile-barcodes/barcode-dialog/barcode-dialog.component';
import { MobileBarcodesComponent } from './mobile-barcodes/mobile-barcodes.component';
import { CanEditExperimentPipe } from './pipes/can-edit-experiment.pipe';
import { HasAnyICRolesPipe } from './pipes/has-any-ic-roles.pipe';
import { ICExperimentFormatPipe } from './pipes/icexperiment-format.pipe';
import { ICExperimentStatusIsPipe } from './pipes/icexperiment-status-is.pipe';
import { TimeAgoPipe } from './pipes/timeago.pipe';
import { ResultsLinksComponent } from './results/results-links/results-links.component';
import { ResultsSummaryComponent } from './results/results-summary/results-summary.component';
import { SampleSummaryDialog } from './samples/sample-list/sample-summary/sample-summary.component';
import { LabpartnerService } from './services/labpartner.service';

import { definePreset } from '@primeuix/themes';
import Material from '@primeuix/themes/material';

const MaterialIndigo = definePreset(Material, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
});

export default MaterialIndigo;

const permissionPipes = [
  HasAnyRolesPipe,
  HasAnyICRolesPipe,
  ExperimentStatusIsPipe,
  ExperimentStatusIsNotPipe,
  CanEditExperimentPipe,
  ICExperimentStatusIsPipe,
];

@NgModule({
  declarations: [
    AppComponent,
    LoaderComponent,
    ExperimentDialogComponent,
    ExperimentListComponent,
    MatConfirmDialogComponent,
    ExperimentDetailComponent,
    ICExperimentDetailComponent,
    SampleListComponent,
    ConditionListComponent,
    XlsImportComponent,
    CsvFormComponent,
    DetailListComponent,
    LogisticListComponent,
    MobileBarcodesComponent,
    BarcodeDisplayComponent,
    NoteDisplayComponent,
    UserDisplayComponent,
    CellEditDisplayComponent,
    DetailListPrintComponent,
    LogisticComponent,
    ScanDisplayComponent,
    BarcodePrintComponent,
    DetailListPrintBarcodesComponent,
    DetailListPrintAveryComponent,
    NotesDialogComponent,
    MatAckDialogComponent,
    NoAccessComponent,
    FileUploadComponent,
    DetailListPrintZebraComponent,
    BundleListComponent,
    BundleComponent,
    AdvancedBundleDialogComponent,
    SavannaResultsComponent,
    ResultsSummaryComponent,
    DetailsResultsComponent,
    ChooseExperimentComponent,
    InputsComponent,
    ExperimentFormatPipe,
    ICExperimentFormatPipe,
    AssayTypeCodeFormatPipe,
    TimeAgoPipe,
    BundleAssayTypeFormatPipe,
    ChangesDialogComponent,
    AuditDialogComponent,
    ReleaseNotesDialogComponent,
    SavannaConfigComponent,
    SavannaCreateConfigDialogComponent,
    AddColumnDialogComponent,
    SavannaResultsImgSrcPipe,
    BarcodeDialogComponent,
    ColumnFilterComponent,
    ...permissionPipes,
    ExperimentListForBundleComponent,
    InstrumentErrorDialogComponent,
    DeviceTargetErrorDialogComponent,
    DeviceTargetErrorChannelDialogComponent,
    ConditionsPrecisionGenerationComponent,
    ManageAssaysComponent,
    AssayDialogComponent,
    AutomatedReportsComponent,
    FixDuplicateResultsComponent,
    SelectExternalControlComponent,
    ContactAndSupportDialogComponent,
    SampleSummaryDialog,
    ResultsLinksComponent,
    ICExperimentsComponent,
    ICExperimentDialogComponent,
    GroupDropdownComponent,
    ICSyncDeviceComponent,
    ICSequenceComponent,
    ICAdjustLegendComponent,
    ICImagesDialogComponent,
    ICExcludeIncludeDialogComponent,
    ReplaceInstrumentComponent,
    IntegerOnlyDirective,
    TestLoginDialogComponent,
  ],
  bootstrap: environment.testMode ? [AppComponent] : [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    ReactiveFormsModule,
    FormsModule,
    AdvancedBundleEditComponent,
  ],
  providers: [
    providePrimeNG({
      theme: { preset: MaterialIndigo, options: { darkModeSelector: false } },
      ripple: true,
    }),
    // Conditional MSAL providers - mock in test mode, real in normal mode
    ...(environment.testMode
      ? [
          {
            provide: MsalService,
            useFactory: (testMsalService: TestMsalService) => ({
              instance: {
                getActiveAccount: () => testMsalService.getActiveAccount(),
                getAllAccounts: () => testMsalService.getAllAccounts(),
                getAccount: () => null,
                setActiveAccount: () => testMsalService.setActiveAccount(),
                clearCache: () => testMsalService.clearCache(),
              },
              loginRedirect: () => testMsalService.loginRedirect(),
              logout: () => testMsalService.logout(),
              acquireTokenSilent: () => testMsalService.acquireTokenSilent(),
              acquireTokenPopup: () => testMsalService.acquireTokenPopup(),
              acquireTokenRedirect: () => testMsalService.acquireTokenRedirect(),
              handleRedirectPromise: () => testMsalService.handleRedirectPromise(),
              setActiveAccount: () => testMsalService.setActiveAccount(),
            }),
            deps: [TestMsalService],
          },
          {
            provide: MsalBroadcastService,
            useFactory: (testMsalService: TestMsalService) => ({
              msalSubject: {
                subscribe: () => ({ unsubscribe: () => {} }),
              },
              inProgress$: testMsalService.inProgress$,
              msalSubject$: new BehaviorSubject({}),
            }),
            deps: [TestMsalService],
          },
          {
            provide: MsalGuard,
            useValue: {
              canActivate: () => true,
              canActivateChild: () => true,
            },
          },
        ]
      : [MsalService, MsalBroadcastService, MsalGuard]),
    ExperimentService,
    LoggingService,
    MatConfirmDialogComponent,
    BundleService,
    ExperimentFormatPipe,
    ICExperimentFormatPipe,
    AssayTypeCodeFormatPipe,
    CanEditExperimentPipe,
    ChangesDialogComponent,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: environment.testMode ? TestTokenInterceptor : MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestCountInterceptor,
      deps: [LabpartnerService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    DatePipe,
    BundleAssayTypeFormatPipe,
    ...permissionPipes,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
